const CORRELATION_ID_HEADER = "x-conciergeco-correlation-id";
const CORRELATION_ID_LOG_FIELD = "correlation_id";
const OBSERVABILITY_CORRELATION_ID_DB_FIELD = "observability_correlation_id";
const CORRELATION_ID_CONTEXT_KEY = "__conciergecoCorrelationId";
const CORRELATION_ID_ERROR_SYMBOL = Symbol.for(
  "conciergeco.observabilityCorrelationId",
);

const CORRELATION_ID_PATTERN = /^CW[PSVD]_[0-9a-f]{32}$/u;

type CorrelationEnvironment =
  | "development"
  | "local"
  | "preview"
  | "production"
  | "staging"
  | "test";
type CorrelationPrefix = "CWD" | "CWP" | "CWS" | "CWV";
type CorrelationDecisionStatus = "accepted" | "generated" | "replaced";
type CorrelationDecisionReason = "malformed" | "missing" | "wrong_environment";

type CorrelationDecision = {
  correlationId: string;
  reason?: CorrelationDecisionReason;
  status: CorrelationDecisionStatus;
};

type CorrelationRandomSource = {
  randomUUID: () => string;
};
type RuntimeCryptoLike = {
  randomUUID?: () => string;
};

type ResolveCorrelationIdInput = {
  environment: string | null | undefined;
  incomingId?: string | null;
  randomSource?: CorrelationRandomSource;
};

type HeaderLike =
  | Headers
  | Record<string, readonly string[] | string | null | undefined>;

type CorrelationContext = Record<PropertyKey, unknown>;

function isHeaders(value: HeaderLike): value is Headers {
  return typeof (value as Headers).get === "function";
}

function normalizeHeaderValue(
  value: readonly string[] | string | null | undefined,
): string | null {
  if (typeof value === "string") {
    return value;
  }

  if (value === null || value === undefined) {
    return null;
  }

  return value[0] ?? null;
}

function readCorrelationIdHeader(headers: HeaderLike): string | null {
  if (isHeaders(headers)) {
    return headers.get(CORRELATION_ID_HEADER);
  }

  return normalizeHeaderValue(
    headers[CORRELATION_ID_HEADER] ??
      headers[CORRELATION_ID_HEADER.toLowerCase()] ??
      headers[CORRELATION_ID_HEADER.toUpperCase()],
  );
}

function normalizeCorrelationEnvironment(
  environment: string | null | undefined,
  fallback: CorrelationEnvironment = "development",
): CorrelationEnvironment {
  switch (environment?.trim().toLowerCase()) {
    case "production":
    case "prod":
      return "production";
    case "staging":
    case "stage":
      return "staging";
    case "preview":
      return "preview";
    case "development":
    case "dev":
      return "development";
    case "local":
      return "local";
    case "test":
      return "test";
    default:
      return fallback;
  }
}

function getCorrelationPrefix(environment: string | null | undefined): CorrelationPrefix {
  switch (normalizeCorrelationEnvironment(environment)) {
    case "production":
      return "CWP";
    case "staging":
      return "CWS";
    case "preview":
      return "CWV";
    case "development":
    case "local":
    case "test":
      return "CWD";
  }
}

function normalizeCorrelationId(value: string | null | undefined): string | null {
  const normalized = value?.trim() ?? "";

  return CORRELATION_ID_PATTERN.test(normalized) ? normalized : null;
}

function getCorrelationIdPrefix(value: string): CorrelationPrefix | null {
  const normalized = normalizeCorrelationId(value);

  if (!normalized) {
    return null;
  }

  return normalized.slice(0, 3) as CorrelationPrefix;
}

function isCorrelationIdForEnvironment(
  value: string | null | undefined,
  environment: string | null | undefined,
) {
  const prefix = value ? getCorrelationIdPrefix(value) : null;

  return prefix !== null && prefix === getCorrelationPrefix(environment);
}

function createCorrelationIdSuffix(randomSource: CorrelationRandomSource) {
  const suffix = randomSource.randomUUID().replaceAll("-", "").toLowerCase();

  if (!/^[0-9a-f]{32}$/u.test(suffix)) {
    throw new Error("Correlation random source did not return UUIDv4-shaped hex.");
  }

  return suffix;
}

function getDefaultRandomSource(): CorrelationRandomSource {
  const runtimeCrypto = (globalThis as { crypto?: RuntimeCryptoLike }).crypto;
  const randomUUID = runtimeCrypto?.randomUUID;

  if (typeof randomUUID !== "function") {
    throw new Error("Correlation ID generation requires crypto.randomUUID().");
  }

  return {
    randomUUID: () => randomUUID.call(runtimeCrypto),
  };
}

function generateCorrelationId(
  environment: string | null | undefined,
  randomSource: CorrelationRandomSource = getDefaultRandomSource(),
) {
  return `${getCorrelationPrefix(environment)}_${createCorrelationIdSuffix(randomSource)}`;
}

function resolveCorrelationId({
  environment,
  incomingId,
  randomSource,
}: ResolveCorrelationIdInput): CorrelationDecision {
  const normalizedIncomingId = incomingId?.trim() ?? "";

  if (normalizedIncomingId.length === 0) {
    return {
      correlationId: generateCorrelationId(environment, randomSource),
      reason: "missing",
      status: "generated",
    };
  }

  if (!CORRELATION_ID_PATTERN.test(normalizedIncomingId)) {
    return {
      correlationId: generateCorrelationId(environment, randomSource),
      reason: "malformed",
      status: "replaced",
    };
  }

  if (!isCorrelationIdForEnvironment(normalizedIncomingId, environment)) {
    return {
      correlationId: generateCorrelationId(environment, randomSource),
      reason: "wrong_environment",
      status: "replaced",
    };
  }

  return {
    correlationId: normalizedIncomingId,
    status: "accepted",
  };
}

function setCorrelationIdHeader(headers: Headers, correlationId: string) {
  headers.set(CORRELATION_ID_HEADER, correlationId);
}

function setClientCorrelationId(context: CorrelationContext, correlationId: string) {
  Object.defineProperty(context, CORRELATION_ID_CONTEXT_KEY, {
    configurable: true,
    enumerable: false,
    value: correlationId,
    writable: true,
  });
}

function getClientCorrelationId(context: CorrelationContext | null | undefined) {
  const value = context?.[CORRELATION_ID_CONTEXT_KEY];

  return typeof value === "string" ? normalizeCorrelationId(value) : null;
}

function setErrorCorrelationId(error: unknown, correlationId: string | null | undefined) {
  const normalizedCorrelationId = normalizeCorrelationId(correlationId);

  if (
    normalizedCorrelationId === null ||
    (typeof error !== "object" && typeof error !== "function") ||
    error === null
  ) {
    return;
  }

  Object.defineProperty(error, CORRELATION_ID_ERROR_SYMBOL, {
    configurable: true,
    enumerable: false,
    value: normalizedCorrelationId,
    writable: true,
  });
}

function getErrorCorrelationId(
  error: unknown,
  visited = new Set<unknown>(),
): string | null {
  if (
    (typeof error !== "object" && typeof error !== "function") ||
    error === null ||
    visited.has(error)
  ) {
    return null;
  }

  visited.add(error);

  const value = (error as Record<PropertyKey, unknown>)[CORRELATION_ID_ERROR_SYMBOL];
  const normalized = typeof value === "string" ? normalizeCorrelationId(value) : null;

  if (normalized) {
    return normalized;
  }

  return getErrorCorrelationId(
    (error as { cause?: unknown }).cause,
    visited,
  );
}

export {
  CORRELATION_ID_HEADER,
  CORRELATION_ID_LOG_FIELD,
  CORRELATION_ID_PATTERN,
  OBSERVABILITY_CORRELATION_ID_DB_FIELD,
  generateCorrelationId,
  getClientCorrelationId,
  getCorrelationIdPrefix,
  getCorrelationPrefix,
  getErrorCorrelationId,
  isCorrelationIdForEnvironment,
  normalizeCorrelationEnvironment,
  normalizeCorrelationId,
  readCorrelationIdHeader,
  resolveCorrelationId,
  setClientCorrelationId,
  setCorrelationIdHeader,
  setErrorCorrelationId,
  type CorrelationDecision,
  type CorrelationDecisionReason,
  type CorrelationDecisionStatus,
  type CorrelationEnvironment,
  type CorrelationPrefix,
  type ResolveCorrelationIdInput,
};
